import { useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { X, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFundraiserStore } from '../../store/fundraiserStore';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { getOrganizationSports } from '../../lib/api';
import { formatSportLabel } from '../../lib/sportLabel';
import { detectSportMismatch } from '../../lib/fundraiserSportMatch';
import { screenshotsEnabled } from '../../lib/features';
import { ScreenshotStrip } from './ScreenshotStrip';
import type { CampaignStatus, CompetitorFundraiser, FundraiserUpdatePayload, OrgSportOption } from '../../types';

interface EditFundraiserModalProps {
  fundraiser: CompetitorFundraiser | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onScreenshotsChanged?: () => void;
}

const STATUS_OPTIONS: { value: CampaignStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ENDED', label: 'Ended' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

// raisedAmount/goalAmount/year arrive over the wire as Prisma Decimal strings
// (or already-normalized numbers from the store) — this normalizes either
// shape, and blank/null both collapse to null so a blank field never diffs
// as "changed" against a null snapshot value.
const toNum = (v: string | number | null | undefined): number | null =>
  v == null || v === '' ? null : Number(v);

const amountValid = (n: number | null): boolean => n === null || (Number.isFinite(n) && n >= 0);
const yearValid = (n: number | null): boolean =>
  n === null || (Number.isInteger(n) && n >= 2020 && n <= 2030);

interface Snapshot {
  raisedAmount: string | number | null;
  goalAmount: string | number | null;
  status: CampaignStatus;
  year: string | number | null;
  sportId: string | null;
}

/**
 * Edit a saved competitor fundraiser — all-roles-safe fields only (amounts,
 * status, year, and the sport it's filed under at its school). Refresh is page-owned:
 * `onSuccess` is fire-and-forget and never awaited here, so the page can
 * close this modal and kick off its own list/stats refresh without routing
 * a refresh error into a dialog that's already gone.
 */
export const EditFundraiserModal = ({ fundraiser, isOpen, onClose, onSuccess, onScreenshotsChanged }: EditFundraiserModalProps) => {
  const { updateFundraiser } = useFundraiserStore();

  const [raisedAmount, setRaisedAmount] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [status, setStatus] = useState<CampaignStatus>('UNKNOWN');
  const [year, setYear] = useState('');
  const [sportId, setSportId] = useState('');
  const [sportOptions, setSportOptions] = useState<OrgSportOption[]>([]);
  const [isLoadingSports, setIsLoadingSports] = useState(false);
  const [dismissedMismatch, setDismissedMismatch] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Screenshots upload/remove immediately (not on Save), so adding or removing one
  // counts as a change that enables Save and lets the user finish.
  const [screenshotsTouched, setScreenshotsTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleId = useId();
  const raisedId = useId();
  const goalId = useId();
  const statusId = useId();
  const yearId = useId();
  const sportSelectId = useId();

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const isSavingRef = useRef(isSaving);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Closed → open transition: snapshot the starting values (for the diff)
  // and reset error/isSaving so a prior save's leftovers never leak in.
  useEffect(() => {
    if (!isOpen || !fundraiser) return;
    setRaisedAmount(fundraiser.raisedAmount != null ? String(fundraiser.raisedAmount) : '');
    setGoalAmount(fundraiser.goalAmount != null ? String(fundraiser.goalAmount) : '');
    setStatus(fundraiser.status);
    setYear(fundraiser.year != null ? String(fundraiser.year) : '');
    setSportId(fundraiser.sportId ?? '');
    setDismissedMismatch(null);
    setSnapshot({
      raisedAmount: fundraiser.raisedAmount,
      goalAmount: fundraiser.goalAmount,
      status: fundraiser.status,
      year: fundraiser.year,
      sportId: fundraiser.sportId,
    });
    setError(null);
    setIsSaving(false);
    setScreenshotsTouched(false);
  }, [isOpen, fundraiser]);

  // The school's sports (hidden-on-page ones included) for the sport picker.
  const orgId = fundraiser?.organizationId ?? null;
  useEffect(() => {
    if (!isOpen || !orgId) {
      setSportOptions([]);
      setIsLoadingSports(false);
      return;
    }
    let cancelled = false;
    setIsLoadingSports(true);
    getOrganizationSports(orgId)
      .then((sports) => { if (!cancelled) setSportOptions(sports ?? []); })
      .catch(() => { if (!cancelled) setSportOptions([]); })
      .finally(() => { if (!cancelled) setIsLoadingSports(false); });
    return () => { cancelled = true; };
  }, [isOpen, orgId]);

  // Dialog accessibility: focus the first field on open, Escape-to-close
  // (guarded against mid-save), and restore focus to whatever was focused
  // before the dialog opened. Deps are [isOpen] ONLY — isSaving/onClose are
  // read through refs so an identity change in either doesn't re-capture
  // `prev` or reinstall the listener while the dialog stays open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.activeElement as HTMLElement | null;
    firstInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSavingRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [isOpen]);

  useFocusTrap(dialogRef, isOpen);

  if (!isOpen || !fundraiser) return null;

  const raisedNum = toNum(raisedAmount);
  const goalNum = toNum(goalAmount);
  const yearNum = toNum(year);
  const valid = amountValid(raisedNum) && amountValid(goalNum) && yearValid(yearNum);

  const changed: FundraiserUpdatePayload = {};
  if (snapshot) {
    if (toNum(snapshot.raisedAmount) !== raisedNum) changed.raisedAmount = raisedNum;
    if (toNum(snapshot.goalAmount) !== goalNum) changed.goalAmount = goalNum;
    if (snapshot.status !== status) changed.status = status;
    if (toNum(snapshot.year) !== yearNum) changed.year = yearNum;
    if ((snapshot.sportId ?? '') !== sportId) changed.sportId = sportId || null;
  }
  const hasFieldChanges = Object.keys(changed).length > 0;
  const hasChanges = hasFieldChanges || screenshotsTouched;

  // Keep the current sport selectable even if the school's list no longer
  // carries it (e.g. it was deactivated), so opening never silently changes it.
  const pickerSports: OrgSportOption[] =
    fundraiser.sportId && !sportOptions.some((s) => s.id === fundraiser.sportId) && !isLoadingSports
      ? [{ id: fundraiser.sportId, name: fundraiser.sport ?? 'Current sport', gender: 'COED' }, ...sportOptions]
      : sportOptions;
  const chosenSport = sportOptions.find((s) => s.id === sportId);
  const chosenSportLabel = chosenSport ? formatSportLabel(chosenSport.gender, chosenSport.name) : '';
  const mismatch = detectSportMismatch({
    chosenSportId: sportId || null,
    sports: sportOptions,
    campaignName: fundraiser.campaignName,
  });
  const showMismatch = mismatch !== null && mismatch.detectedLabel !== dismissedMismatch;

  const handleSave = async () => {
    if (!valid || !hasChanges) return;
    setIsSaving(true);
    setError(null);
    try {
      // Screenshot-only edits are already saved server-side; nothing to PATCH.
      if (hasFieldChanges) await updateFundraiser(fundraiser.id, changed);
      toast.success('Fundraiser updated');
      onSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(typeof message === 'string' && message ? message : 'Failed to update fundraiser');
    } finally {
      setIsSaving(false);
    }
  };

  const orgName = fundraiser.organization?.name ?? fundraiser.schoolName;
  const orgState = fundraiser.organization?.state ?? fundraiser.state;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => {
          if (!isSaving) onClose();
        }}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full"
        >
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-start justify-between">
            <div>
              <h2 id={titleId} className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Edit Fundraiser
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {orgName}
                {orgState ? ` — ${orgState}` : ''}
              </p>
              {fundraiser.campaignName && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{fundraiser.campaignName}</p>
              )}
              {fundraiser.campaignUrl && (
                <a
                  href={fundraiser.campaignUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blast-600 dark:text-blast-400 hover:underline inline-flex items-center gap-1 mt-1"
                >
                  View campaign <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor={raisedId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount Raised
                </label>
                <input
                  ref={firstInputRef}
                  id={raisedId}
                  type="number"
                  value={raisedAmount}
                  onChange={(e) => setRaisedAmount(e.target.value)}
                  className="input"
                  placeholder="0"
                  min={0}
                  step="0.01"
                  disabled={isSaving}
                />
              </div>

              <div>
                <label htmlFor={goalId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Goal Amount
                </label>
                <input
                  id={goalId}
                  type="number"
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(e.target.value)}
                  className="input"
                  placeholder="0"
                  min={0}
                  step="0.01"
                  disabled={isSaving}
                />
              </div>

              <div>
                <label htmlFor={statusId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  id={statusId}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as CampaignStatus)}
                  className="input"
                  disabled={isSaving}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor={yearId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Year
                </label>
                <input
                  id={yearId}
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="input"
                  min={2020}
                  max={2030}
                  disabled={isSaving}
                />
              </div>
            </div>

            {fundraiser.organizationId && (
              <div>
                <label htmlFor={sportSelectId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sport
                </label>
                <select
                  id={sportSelectId}
                  value={sportId}
                  onChange={(e) => setSportId(e.target.value)}
                  className="input"
                  disabled={isSaving || isLoadingSports}
                >
                  <option value="">No sport / Other</option>
                  {pickerSports.map((s) => (
                    <option key={s.id} value={s.id}>{formatSportLabel(s.gender, s.name)}</option>
                  ))}
                </select>
              </div>
            )}

            {screenshotsEnabled() && (
              <ScreenshotStrip
                fundraiserId={fundraiser.id}
                onChanged={() => {
                  setScreenshotsTouched(true);
                  onScreenshotsChanged?.();
                }}
              />
            )}

            {showMismatch && mismatch && (
              <div
                role="alert"
                className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-2"
              >
                <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This looks like a <span className="font-medium">{mismatch.detectedLabel}</span> fundraiser, but it's filed under <span className="font-medium">{chosenSportLabel}</span>.
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 pl-6">
                  {mismatch.suggestion && (
                    <button
                      type="button"
                      onClick={() => setSportId(mismatch.suggestion!.id)}
                      className="btn-primary text-sm"
                    >
                      Switch to {formatSportLabel(mismatch.suggestion.gender, mismatch.suggestion.name)}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setDismissedMismatch(mismatch.detectedLabel)}
                    className="btn-secondary text-sm"
                  >
                    Keep {chosenSportLabel}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={isSaving}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
              disabled={isSaving || !valid || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
